import {
  Badge,
  Button,
  CheckBox,
  Icon,
  Input,
  MessagePanel,
  RadioButtonList,
  Select,
  SelectOption,
} from '@dittolive/anvil'
import { yupResolver } from '@hookform/resolvers/yup'
import { MinusCircleIcon } from '@phosphor-icons/react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useForm,
  useFormContext,
  useFormState,
  useWatch,
} from 'react-hook-form'

import { MongoConnectorFeatureFlags } from '../../../types/mongoConnector'
import {
  ConnectorFormValues,
  createCollectionSchema,
  MongoCollectionFormValue,
} from './validation'

type CombinedField = { name: string; type: 'ID' | 'COUNTER' }

const getTypeOptions = (dqlStrictMode: boolean): SelectOption[] => [
  { label: 'ID', value: 'ID' },
  { label: 'Counter', value: 'COUNTER', disabled: dqlStrictMode },
]

const toCombinedFields = (
  idFields: string[],
  counters: string[],
): CombinedField[] => [
  ...idFields.map((f) => ({ name: f, type: 'ID' as const })),
  ...counters.map((f) => ({ name: f, type: 'COUNTER' as const })),
]

const fromCombinedFields = (fields: CombinedField[]) => ({
  idMappingFields: fields.filter((f) => f.type === 'ID').map((f) => f.name),
  settableCounters: fields
    .filter((f) => f.type === 'COUNTER')
    .map((f) => f.name),
})

export type CollectionFormProps = {
  /** Index of the collection being edited (null for new) */
  index: number | null
  /** Existing collection data when editing */
  editingCollection?: MongoCollectionFormValue | null
  /** Callback when form is submitted successfully */
  onSave: (value: MongoCollectionFormValue) => void
  /** Callback when form is cancelled */
  onCancel: () => void
  /** Feature flags that affect form behavior */
  featureFlags?: MongoConnectorFeatureFlags
  /** Form ID for external submit button binding */
  formId?: string
  /** Whether the form is currently visible (for reset timing) */
  isActive?: boolean
}

export default function CollectionForm({
  index,
  editingCollection,
  onSave,
  // onCancel,
  featureFlags,
  formId = 'collectionForm',
  isActive = true,
}: CollectionFormProps) {
  const mongoDqlStrictMode = featureFlags?.mongoDqlStrictMode ?? false
  const enableSettableCounters = featureFlags?.enableSettableCounters ?? false

  const parentForm = useFormContext<ConnectorFormValues>()
  const existingCollectionNames = useMemo(
    () => parentForm.watch('collections').map((collection) => collection.name),
    [parentForm],
  )
  const schema = useMemo(
    () => createCollectionSchema(existingCollectionNames, index),
    [existingCollectionNames, index],
  )
  const form = useForm<MongoCollectionFormValue>({
    defaultValues: {
      name: editingCollection?.name || '',
      matchIDs: editingCollection?.matchIDs ?? true,
      initialSync: editingCollection?.initialSync ?? false,
      idMappingFields: editingCollection?.idMappingFields || ['_id'],
      mode: editingCollection?.mode || 'NATIVE',
      dittoTypes: editingCollection?.dittoTypes || { settableCounters: [] },
    },
    resolver: yupResolver(schema),
    resetOptions: { keepDefaultValues: false },
  })

  const { matchIDs, dqlStrictMode } = useWatch({
    control: form.control,
    exact: true,
  })

  const [combinedFields, setCombinedFields] = useState<CombinedField[]>(() =>
    toCombinedFields(
      editingCollection?.idMappingFields || [''],
      enableSettableCounters
        ? editingCollection?.dittoTypes?.settableCounters || []
        : [],
    ),
  )

  // Errors
  const { errors } = useFormState({
    control: form.control,
  })
  const idFieldErrors = errors.idMappingFields
  const counterFieldErrors = errors.dittoTypes?.settableCounters
  const idFieldsArrayError =
    idFieldErrors && 'message' in idFieldErrors
      ? (idFieldErrors.message as string)
      : undefined

  useEffect(() => {
    if (editingCollection) {
      form.reset({
        name: editingCollection?.name || '',
        matchIDs: editingCollection?.matchIDs ?? true,
        initialSync: editingCollection?.initialSync ?? false,
        dqlStrictMode: editingCollection?.dqlStrictMode ?? false,
        idMappingFields: editingCollection?.idMappingFields || [''],
        mode: editingCollection?.mode || 'NATIVE',
        dittoTypes: editingCollection?.dittoTypes || { settableCounters: [] },
      })
      setCombinedFields(
        toCombinedFields(
          editingCollection?.idMappingFields || [''],
          enableSettableCounters
            ? editingCollection?.dittoTypes?.settableCounters || []
            : [],
        ),
      )
    } else {
      const timeout = setTimeout(() => {
        form.reset({
          name: '',
          matchIDs: true,
          initialSync: false,
          dqlStrictMode: false,
          mode: 'NATIVE',
          idMappingFields: ['_id'],
          dittoTypes: { settableCounters: [] },
        })
        setCombinedFields([{ name: '', type: 'ID' }])
      }, 500)
      return () => clearTimeout(timeout)
    }
  }, [form, editingCollection, isActive, enableSettableCounters])

  const handleChangeMatchIDs = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newMatchIDs = e.target.value === 'matchIDs'
      form.setValue('matchIDs', newMatchIDs)
      if (newMatchIDs) {
        // Switching to auto-generated mode - set idMappingFields to ['_id']
        form.setValue('idMappingFields', ['_id'])
        form.setValue('dittoTypes.settableCounters', [])
      } else {
        // Switching to custom mapping - initialize with empty ID field
        setCombinedFields([{ name: '', type: 'ID' }])
        form.setValue('idMappingFields', [''])
        form.setValue('dittoTypes.settableCounters', [])
      }
    },
    [form],
  )

  const syncCombinedFieldsToForm = useCallback(
    (fields: CombinedField[]) => {
      const { idMappingFields, settableCounters } = fromCombinedFields(fields)
      form.setValue(
        'idMappingFields',
        idMappingFields.length ? idMappingFields : [''],
        { shouldValidate: true, shouldDirty: true },
      )
      form.setValue('dittoTypes.settableCounters', settableCounters, {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [form],
  )

  const updateCombinedField = useCallback(
    (idx: number, update: Partial<CombinedField>) => {
      setCombinedFields((prev) => {
        const updated = prev.map((field, i) =>
          i === idx ? { ...field, ...update } : field,
        )
        syncCombinedFieldsToForm(updated)
        return updated
      })
    },
    [syncCombinedFieldsToForm],
  )

  const removeCombinedField = useCallback(
    (idx: number) => {
      setCombinedFields((prev) => {
        const updated = prev.filter((_, i) => i !== idx)
        // Ensure at least one field remains
        const result = updated.length
          ? updated
          : [{ name: '', type: 'ID' as const }]
        syncCombinedFieldsToForm(result)
        return result
      })
    },
    [syncCombinedFieldsToForm],
  )

  const addCombinedField = useCallback(() => {
    setCombinedFields((prev) => {
      const updated = [...prev, { name: '', type: 'ID' as const }]
      syncCombinedFieldsToForm(updated)
      return updated
    })
  }, [syncCombinedFieldsToForm])

  // Feature flag text changes for match IDs selection
  const matchIdsTitle = useMemo(() => {
    if (enableSettableCounters) {
      return (
        <>
          Auto-generated document schema
          <Badge colorScheme="sunset" size="sm" className="ml-2">
            Development only
          </Badge>
        </>
      )
    } else {
      return 'Match IDs'
    }
  }, [enableSettableCounters])
  const matchIdsDescription = useMemo(() => {
    return enableSettableCounters
      ? 'Automatically map MongoDB fields with Ditto fields'
      : 'Match MongoDB document IDs with Ditto document IDs'
  }, [enableSettableCounters])

  // Feature flag text changes for the field mapping selection
  const fieldMappingTitle = useMemo(() => {
    return enableSettableCounters
      ? 'Custom document schema'
      : 'Map fields to Ditto ID'
  }, [enableSettableCounters])
  const fieldMappingDescription = useMemo(() => {
    return enableSettableCounters
      ? 'Custom map MongoDB types to Ditto types'
      : 'Map MongoDB fields to Ditto document IDs'
  }, [enableSettableCounters])

  return (
    <form
      id={formId}
      className="w-full"
      onSubmit={form.handleSubmit(onSave)}
      data-testid="collectionForm"
    >
      <fieldset
        className="flex flex-col gap-8"
        disabled={form.formState.isSubmitting}
      >
        <Input
          id="name"
          label="Collection to sync"
          description="Name of an existing MongoDB collection"
          placeholder="collection-name"
          containerClassName="grow"
          data-1p-ignore
          errorMessage={errors.name?.message}
          {...form.register('name')}
          data-testid="collectionName"
        />

        <div className="space-y-4">
          <CheckBox
            id="initial_sync"
            description="Synchronize all existing documents from the MongoDB collection into Ditto."
            onCheckedChange={(checked) =>
              form.setValue('initialSync', !!checked)
            }
            checked={!!form.watch('initialSync')}
            label="Enable initial sync"
            data-testid="initialSyncCheckbox"
          />

          {mongoDqlStrictMode && (
            <CheckBox
              id="dql_strict_mode"
              description={
                <>
                  Registers do not support BSON. Uncheck to sync{' '}
                  <a
                    href="https://www.mongodb.com/docs/manual/reference/mongodb-extended-json/#bson-data-types-and-associated-representations"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-foreground-normal underline"
                  >
                    MongoDB BSON types
                  </a>
                  .
                </>
              }
              onCheckedChange={(checked) => {
                if (checked) {
                  form.setValue('mode', 'NATIVE')
                  // Remove counter fields - counters are not supported in strict mode
                  form.setValue('dittoTypes.settableCounters', [])
                  // Only update combined fields when in custom mapping mode
                  if (!form.getValues('matchIDs')) {
                    setCombinedFields((prev) => {
                      const updated = prev.filter(
                        (field) => field.type !== 'COUNTER',
                      )
                      // Ensure at least one ID field remains
                      const result = updated.length
                        ? updated
                        : [{ name: '', type: 'ID' as const }]

                      syncCombinedFieldsToForm(result)
                      return result
                    })
                  }
                }
                form.setValue('dqlStrictMode', !!checked)
              }}
              checked={!!form.watch('dqlStrictMode')}
              label="Enable DQL Strict Mode"
              data-testid="dqlStrictModeCheckbox"
            />
          )}

          <CheckBox
            id="mode"
            description={
              <>
                Store data in{' '}
                <a
                  href="https://www.mongodb.com/docs/manual/reference/mongodb-extended-json/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-foreground-normal underline"
                >
                  Extended JSON v2 format
                </a>{' '}
                to preserve MongoDB BSON types.
              </>
            }
            disabled={!!form.watch('dqlStrictMode')}
            onCheckedChange={(checked) =>
              form.setValue('mode', checked ? 'EJSON' : 'NATIVE')
            }
            checked={form.watch('mode') === 'EJSON'}
            label="Enable canonical Extended JSON v2 format"
            data-testid="modeCheckbox"
          />
        </div>

        <RadioButtonList<'matchIDs' | 'customMapping'>
          options={['matchIDs', 'customMapping']}
          keyFn={(option) => option}
          renderFn={(option) => ({
            title: option === 'matchIDs' ? matchIdsTitle : fieldMappingTitle,
            description:
              option === 'matchIDs'
                ? matchIdsDescription
                : fieldMappingDescription,
          })}
          value={matchIDs ? 'matchIDs' : 'customMapping'}
          onChange={handleChangeMatchIDs}
          htmlFor="matchIDs"
          orientation="vertical"
        />

        {!matchIDs && (
          <div className="flex flex-col gap-2">
            {dqlStrictMode && (
              <MessagePanel
                variant="info"
                message="Counters are not available in DQL Strict Mode."
              />
            )}

            {combinedFields.map((field, idx) => {
              const isIdField = field.type === 'ID'
              const idFieldIndex = combinedFields
                .slice(0, idx)
                .filter((f) => f.type === 'ID').length
              const counterFieldIndex = combinedFields
                .slice(0, idx)
                .filter((f) => f.type === 'COUNTER').length

              const fieldError = isIdField
                ? idFieldErrors?.[idFieldIndex]?.message
                : counterFieldErrors?.[counterFieldIndex]?.message

              return (
                <div
                  key={`combinedField-${idx}`}
                  className="flex w-full items-start gap-2"
                >
                  <div className="flex grow flex-col gap-1">
                    {idx === 0 && (
                      <p className="text-foreground-normal font-medium">
                        MongoDB field name
                      </p>
                    )}

                    <Input
                      id={`combinedField.${idx}.name`}
                      placeholder="MongoDB field name"
                      containerClassName="grow"
                      value={field.name}
                      onChange={(e) =>
                        updateCombinedField(idx, { name: e.target.value })
                      }
                      errorMessage={fieldError}
                      data-testid="combinedField"
                    />
                  </div>

                  {enableSettableCounters && (
                    <div className="flex w-32 flex-col gap-1">
                      {idx === 0 && (
                        <p className="text-foreground-normal font-medium">
                          Ditto type
                        </p>
                      )}

                      <Select
                        id={`combinedField.${idx}.type`}
                        options={getTypeOptions(!!dqlStrictMode)}
                        value={field.type}
                        onValueChange={(value) =>
                          updateCombinedField(idx, {
                            type: value as 'ID' | 'COUNTER',
                          })
                        }
                        containerClassName="w-full shrink-0"
                        data-testid="combinedFieldType"
                      />
                    </div>
                  )}

                  <div className="flex shrink-0 flex-col gap-1">
                    {idx === 0 && (
                      // A bit hacky, but keeps the layout consistent
                      <div className="h-5.25" aria-hidden="true" />
                    )}

                    <Button
                      title="Remove field"
                      className="size-8 p-0"
                      onClick={() => removeCombinedField(idx)}
                      disabled={combinedFields.length <= 1}
                      data-testid="removeCombinedFieldButton"
                    >
                      <Icon
                        svg={<MinusCircleIcon />}
                        className="text-foreground-subtle size-4"
                      />
                    </Button>
                  </div>
                </div>
              )
            })}

            <Button
              variant="primary"
              onClick={addCombinedField}
              className="self-start"
              data-testid="addCombinedFieldButton"
            >
              Add
            </Button>

            {idFieldsArrayError && (
              <MessagePanel variant="danger" message={idFieldsArrayError} />
            )}
          </div>
        )}
      </fieldset>
    </form>
  )
}
